<template>
  <div ref="containerRef" class="col fit relative-position overflow-hidden">
    <v-stage
      class="absolute-top-left"
      v-if="stageConfig.width > 0 && stageConfig.height > 0"
      :config="stageConfig"
    >
      <v-layer>
        <!-- Field Rendering (Background, Dirt, Diamond, etc.) -->
        <v-rect :config="backgroundConfig" />
        <v-wedge :config="outfieldWedgeConfig" />
        <v-wedge :config="infieldDirtConfig" />
        <v-regular-polygon :config="diamondConfig" />
        <v-regular-polygon :config="homePlateConfig" />

        <!-- FIELDERS -->
        <v-group
          v-for="fielder in fielders"
          :key="'obs-fielder-' + fielder.position"
          :config="{
            x: fielder.x,
            y: fielder.y,
            onClick: (e: any) => handleTap(fielder.playerId, e),
            onTap: (e: any) => handleTap(fielder.playerId, e),
          }"
        >
          <!-- Highlight Ring -->
          <v-circle
            v-if="selectedPlayerIds.includes(fielder.playerId)"
            :config="{
              radius: 22,
              fill: 'rgba(241, 196, 15, 0.4)',
              stroke: '#f1c40f',
              strokeWidth: 2,
            }"
          />
          <FieldPlayerMarker
            type="fielder"
            :uniform-number="fielder.uniformNumber ?? 0"
            :player-name="showNames ? fielder.name : ''"
            :position="fielder.position"
            :x="0"
            :y="0"
            :opacity="1"
            :show-zone="false"
            :show-position-initials="true"
          />
        </v-group>
      </v-layer>
    </v-stage>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { useFieldLayout } from 'src/helpers/helpers'
import FieldPlayerMarker from 'components/FieldPlayerMarker.vue'
import type { DefenseSnapshot, LineupEntry } from 'stores/base-types'

const props = defineProps<{
  defenseSnapshot: DefenseSnapshot
  lineup: LineupEntry[]
  selectedPlayerIds: number[]
  showNames?: boolean // NEW
}>()

const emit = defineEmits<{ (e: 'toggle-selection', playerId: number): void }>()

// --- CANVAS SETUP ---
const containerRef = ref<HTMLElement | null>(null)
const stageConfig = reactive({ width: 0, height: 0 })
let resizeObserver: ResizeObserver | null = null

const stageSizeRef = computed(() => ({ width: stageConfig.width, height: stageConfig.height }))
const {
  backgroundConfig,
  outfieldWedgeConfig,
  infieldDirtConfig,
  diamondConfig,
  homePlateConfig,
  getStandardPositionCoords,
} = useFieldLayout(stageSizeRef)

onMounted(() => {
  // Add a tiny delay to wait for Quasar's flexbox to finish painting
  setTimeout(() => {
    if (containerRef.value) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          stageConfig.width = entry.contentRect.width
          stageConfig.height = entry.contentRect.height
        }
      })
      resizeObserver.observe(containerRef.value)
    }
  }, 50)
})

onBeforeUnmount(() => {
  if (resizeObserver && containerRef.value) resizeObserver.unobserve(containerRef.value)
})

// --- DATA MAPPING ---
const fielders = computed(() => {
  const arr = []
  const allRoster = props.lineup.concat(props.lineup.flatMap((l) => l.substitutions || []))

  for (const [posStr, state] of Object.entries(props.defenseSnapshot)) {
    const pos = Number(posStr)
    const playerEntry = allRoster.find((p) => p.player.id === state.playerId)
    if (playerEntry && pos >= 1 && pos <= 9) {
      const coords = getStandardPositionCoords(pos)
      arr.push({
        playerId: playerEntry.player.id!,
        position: pos,
        uniformNumber: playerEntry.player.uniformNumber,
        name: playerEntry.player.name, // NEW
        x: coords.x,
        y: coords.y,
        zoneRadius: 0.15,
      })
    }
  }
  return arr
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleTap = (playerId: number, e: any) => {
  if (e && e.cancelBubble !== undefined) e.cancelBubble = true
  emit('toggle-selection', playerId)
}
</script>

<style scoped>
.canvas-container {
  border: none;
  box-sizing: border-box;
  min-height: 220px;
  overflow: hidden;
}
</style>
